import jwt_decode from 'jwt-decode';
import { isNil } from 'lodash';

import { getToken } from '~/modules/token';

export interface Access {
  canLogin: boolean;
  isGlobalAdmin: boolean;
  isGlobalUser: boolean;
  isSystemAdmin: boolean;
  isSuperAdmin: boolean;
  isSuperUser: boolean;
  isAdmin: boolean;
  isStaff: boolean;
  isUser: boolean;
  /** True when an account purview grants AI Manager access (service-ai's gate). */
  canUseAiManager: boolean;
  /** True when an account purview grants Feature flags access. */
  canUseFeatureFlags: boolean;
  /** True when an account purview grants MLS agreements access. */
  canUseMlsAgreements: boolean;
  /** Display name of the highest role held; '' when none. */
  role: string;
}

type RoleList = string[] | string;

/** The slice of the session user that access is derived from. */
export interface AccessUser {
  purview: Record<string, RoleList>;
  customer: { idNumber: number };
}

/** The slice of the decoded bay token that access is derived from. */
interface AccessJwt {
  session: { user: AccessUser };
  /** Refresh-token expiry, seconds since epoch. */
  expRefresh: number;
}

const defaultAccess: Omit<Access, 'role'> = {
  canLogin: false,
  isGlobalAdmin: false,
  isGlobalUser: false,
  isSystemAdmin: false,
  isSuperAdmin: false,
  isSuperUser: false,
  isAdmin: false,
  isStaff: false,
  isUser: false,
  canUseAiManager: false,
  canUseFeatureFlags: false,
  canUseMlsAgreements: false,
};

const AI_MANAGER_PURVIEW = 'AiManager';
const FEATURE_FLAG_PURVIEW = 'FeatureFlag';
const MLS_AGREEMENTS_PURVIEW = 'MlsAgreements';
const ADMIN_ROLES = new Set(['sys', 'globaladmin']);
const AI_PRODUCT_ROLES = new Set(['admin', ...ADMIN_ROLES]);

const roleList = (value: RoleList | undefined): string[] => {
  if (!value) {
    return [];
  }

  return Array.isArray(value) ? value : [value];
};

// service-ai/src/services/account-service/gate.ts normalises the same two legacy spellings.
const normaliseRole = (role: string): string => {
  const lower = role.toLowerCase();

  if (lower === 'systemadmin') {
    return 'sys';
  }

  if (lower === 'superadmin') {
    return 'globaladmin';
  }

  return lower;
};

const hasRole = (value: RoleList | undefined, allowed: Set<string>): boolean =>
  roleList(value).some((role) => allowed.has(normaliseRole(role)));

/**
 * Mirrors service-ai's account gate: any account with an admin/sys/globaladmin
 * role on `AiManager`, or sys/globaladmin on the global `DEFAULT` purview.
 */
export const canUseAiManager = (purview?: Record<string, RoleList> | null): boolean => {
  if (!purview) {
    return false;
  }

  return (
    hasRole(purview[AI_MANAGER_PURVIEW], AI_PRODUCT_ROLES) || hasRole(purview.DEFAULT, ADMIN_ROLES)
  );
};

/** A globalAdmin or systemAdmin role on `FeatureFlag`, or on the global `DEFAULT` purview. */
export const canUseFeatureFlags = (purview?: Record<string, RoleList> | null): boolean => {
  if (!purview) {
    return false;
  }

  return (
    hasRole(purview[FEATURE_FLAG_PURVIEW], ADMIN_ROLES) || hasRole(purview.DEFAULT, ADMIN_ROLES)
  );
};

/** A globalAdmin or systemAdmin role on `MlsAgreements`, or on the global `DEFAULT` purview. */
export const canUseMlsAgreements = (purview?: Record<string, RoleList> | null): boolean => {
  if (!purview) {
    return false;
  }

  return (
    hasRole(purview[MLS_AGREEMENTS_PURVIEW], ADMIN_ROLES) || hasRole(purview.DEFAULT, ADMIN_ROLES)
  );
};

// A missing, malformed, or otherwise undecodable token is treated the same as no token at all.
export const decodeToken = (token: string | undefined): AccessJwt | null => {
  try {
    return jwt_decode<AccessJwt>(token as string);
  } catch {
    return null;
  }
};

export const getAccess = (user?: AccessUser | null): Access => {
  let purviewRecord: Record<string, RoleList> | undefined;
  let customerNumber: number | undefined;

  if (!user) {
    const bayToken = getToken();
    const jwt = decodeToken(bayToken);

    purviewRecord = jwt?.session.user.purview;
    customerNumber = jwt?.session.user.customer.idNumber;
  } else {
    purviewRecord = user?.purview;
    customerNumber = user?.customer.idNumber;
  }

  const purview = roleList(purviewRecord?.Toolbox || purviewRecord?.DEFAULT);
  const access: Access = {
    ...defaultAccess,
    role: '',
    canUseAiManager: canUseAiManager(purviewRecord),
    canUseFeatureFlags: canUseFeatureFlags(purviewRecord),
    canUseMlsAgreements: canUseMlsAgreements(purviewRecord),
  };

  if (purview.length > 0) {
    // User created templates
    access.isAdmin = purview.indexOf('admin') !== -1;
    access.isStaff = purview.indexOf('staff') !== -1;
    access.isUser = purview.indexOf('user') !== -1;

    if (!isNil(customerNumber)) {
      access.isGlobalAdmin = customerNumber === 0 && purview.indexOf('globalAdmin') !== -1;
      access.isGlobalUser = customerNumber === 0 && purview.indexOf('globalUser') !== -1;
      access.isSystemAdmin = customerNumber === 0 && purview.indexOf('sys') !== -1;
      // Can see all templates
      access.isSuperAdmin = customerNumber === 0 && purview.indexOf('superadmin') !== -1;
      // Can only see created templates for customers
      access.isSuperUser = customerNumber === 0 && purview.indexOf('superuser') !== -1;
    }

    access.canLogin = access.isGlobalAdmin || access.isGlobalUser;
  }

  access.role = access.isGlobalAdmin
    ? 'Global admin'
    : access.isGlobalUser
      ? 'Global user'
      : access.isSystemAdmin
        ? 'System admin'
        : access.isSuperAdmin
          ? 'Super admin'
          : access.isSuperUser
            ? 'Super user'
            : access.isAdmin
              ? 'Admin'
              : access.isStaff
                ? 'Staff'
                : access.isUser
                  ? 'User'
                  : '';

  return access;
};
